"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { motion, AnimatePresence } from "motion/react";
import {
  Check,
  Crown,
  Eye,
  Lock,
  Mail,
  Plus,
  Shield,
  SquarePen,
  UserPlus,
  X,
} from "lucide-react";

import { cn } from "../lib/cn";
import { springs } from "../lib/motion";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { Avatar } from "../primitives/Avatar";
import { AvatarGroup, type AvatarGroupItem } from "../primitives/AvatarGroup";
import { Badge, type BadgeProps } from "../primitives/Badge";
import { Button } from "../primitives/Button";
import { Input } from "../primitives/Input";
import { Label } from "../primitives/Label";
import { Skeleton } from "../primitives/Skeleton";
import { aiko } from "../primitives/Toaster";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";

/**
 * MemberPicker — a searchable, keyboard-accessible multi-select for picking
 * workspace members (co-creators / collaborators / share targets).
 *
 * Built on `cmdk` (the command-menu primitive) inside the library's
 * `Popover`, with the chosen members surfaced as an `AvatarGroup` plus
 * removable chips. `cmdk` supplies the filtering, full keyboard navigation
 * (Up / Down / Enter / Esc), `aria-activedescendant`, and `role="listbox" /
 * "option"` semantics — the exact a11y three different apps kept
 * hand-rolling (and the outside-click capture-phase `mousedown` workaround
 * the unified picker fought is gone, because Popover owns focus + dismiss).
 *
 * Fully data-agnostic and controlled: the caller passes `members`, `value`,
 * and `onChange`; the library renders. There is no service import. Identity
 * is the **lowercased email** (matching the donor pickers), so the same
 * person can never be added twice regardless of `id` drift.
 *
 * What it preserves from the donors (unified `CoCreatorPicker`,
 * fe-react `InlineCoCreatorSelector`):
 *  - dedupe-by-lowercased-email; existing members keep their current role,
 *    a new invite takes the picked role ("the role stays the same")
 *  - the inline "Invite New Team Member" row (email + role + Enter-to-commit,
 *    gated on an email regex), and the pending-invite split rendered first
 *  - name + username + email + role are all searchable (the joined haystack)
 *  - four list states: loading (skeletons), error (+ Retry), ok (rows /
 *    empty), with distinct "no members yet" vs "no match" copy
 *  - tinted role badges (editor / admin / owner / viewer), brand tokens only
 *  - async `onAdd` / `onInvite` with optimistic rollback when provided;
 *    synchronous otherwise
 *  - a "Limited access" lock panel when `canManage` is false
 *
 * Every animation (row add / exit, the Popover panel, the invite-commit pop,
 * the AvatarGroup fan) is gated on `useReducedMotion()` with a static
 * fallback, so a reduced-motion user never loses a state signal.
 *
 * @example
 *   const [picked, setPicked] = React.useState<MemberOption[]>([]);
 *   <MemberPicker
 *     members={members}
 *     value={picked}
 *     onChange={setPicked}
 *     onInvite={(email, role) => api.invite(email, role)}
 *     state={isLoading ? "loading" : "ok"}
 *     onRetry={refetch}
 *   />
 */

/* ----------------------------------------------------------------------------
 * Types
 * ------------------------------------------------------------------------- */

export interface MemberOption {
  /** Stable id from the data source. Not the identity key — email is. */
  id: string | number;
  /** Display name. Drives the avatar initials + the primary row label. */
  name: string;
  /** Identity key. Dedupe + add / remove all key off the lowercased value. */
  email: string;
  /** Optional handle, shown in the row subtitle and included in search. */
  username?: string;
  /** Workspace role codename (e.g. "EDITOR"). Searchable + badge source. */
  role?: string;
  /** Avatar image URL. Falls back to initials when absent or it errors. */
  avatar?: string;
  /** Alias for `avatar` — accepted for the lighter `{ id, name, avatarUrl }` shape. */
  avatarUrl?: string;
  /** Override the row subtitle (defaults to `@username · email`). */
  subtitle?: string;
  /** Marks a row as a pending email invite (mail glyph, "Pending invite"). */
  isInvite?: boolean;
}

export interface RoleOption {
  /** Role codename committed onto an invited member. */
  value: string;
  /** Human label shown in the role picker. */
  label: string;
}

const ROLE_META: Record<
  string,
  { label: string; Icon: typeof Crown; tone: NonNullable<BadgeProps["variant"]> }
> = {
  // Tones map the donor's intent onto brand tokens (no raw hex):
  //   editor → success (green), admin → info (blue), owner → warning (dijon),
  //   viewer → secondary (neutral).
  owner: { label: "Owner", Icon: Crown, tone: "warning" },
  admin: { label: "Admin", Icon: Shield, tone: "info" },
  editor: { label: "Editor", Icon: SquarePen, tone: "success" },
  viewer: { label: "Viewer", Icon: Eye, tone: "secondary" },
};

const DEFAULT_INVITE_ROLES: RoleOption[] = [
  { value: "editor", label: "Editor" },
  { value: "admin", label: "Admin" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isEmail = (s: string): boolean => EMAIL_RE.test(s.trim());

/** Map a workspace role codename to the BE-accepted set; default to editor. */
const defaultNormalizeRole = (raw?: string): string =>
  ROLE_META[(raw || "").toLowerCase()] ? (raw as string).toLowerCase() : "editor";

const roleMeta = (raw?: string) => {
  const key = (raw || "").toLowerCase();
  if (ROLE_META[key]) return { ...ROLE_META[key], key };
  return {
    label: raw ? raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase() : "Member",
    Icon: SquarePen,
    tone: "secondary" as NonNullable<BadgeProps["variant"]>,
    key: "",
  };
};

const emailOf = (m: MemberOption): string => (m.email || "").trim();
const avatarOf = (m: MemberOption): string | undefined => m.avatar ?? m.avatarUrl;
const subtitleOf = (m: MemberOption): string =>
  m.subtitle ?? [m.username ? `@${m.username}` : "", emailOf(m)].filter(Boolean).join(" · ");

/* ----------------------------------------------------------------------------
 * Variants — row density / control size
 * ------------------------------------------------------------------------- */

const rowVariants = cva(
  "flex items-center gap-2.5 px-2 outline-none",
  {
    variants: {
      density: {
        comfortable: "py-2",
        compact: "py-1.5",
      },
      size: {
        sm: "text-xs",
        default: "text-sm",
      },
    },
    defaultVariants: { density: "comfortable", size: "default" },
  },
);

/* ----------------------------------------------------------------------------
 * Props
 * ------------------------------------------------------------------------- */

export interface MemberPickerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange">,
    VariantProps<typeof rowVariants> {
  /** The full candidate list (a workspace's members). Data-agnostic. */
  members: MemberOption[];
  /** Currently selected members (controlled). Dedupe is by lowercased email. */
  value: MemberOption[];
  /** Receives the next selection on every add / remove. */
  onChange: (next: MemberOption[]) => void;
  /**
   * Async commit for adding an existing member. When provided, the row is
   * awaited and only committed on success (rolled back on throw). Without
   * it, add is synchronous.
   */
  onAdd?: (member: MemberOption) => Promise<void>;
  /**
   * Commit for inviting a brand-new email. When it returns a promise it is
   * awaited (rolled back on throw). Synchronous otherwise.
   */
  onInvite?: (email: string, role: string) => void | Promise<void>;
  /** Async filter seam: caller-driven search (debounced server query, etc.). */
  onSearch?: (query: string) => void;
  /** Invite-role vocabulary. Default: Editor / Admin. */
  roles?: RoleOption[];
  /** Map a raw role codename to the committed set. Default: known-or-editor. */
  normalizeRole?: (raw?: string) => string;
  /** Exclude-self / exclude-viewer / exclude-owner policy as a predicate. */
  filterMember?: (m: MemberOption) => boolean;
  /** List lifecycle — caller owns the data, the library owns the rendering. */
  state?: "idle" | "loading" | "error" | "ok";
  /** Retry handler shown in the error state. */
  onRetry?: () => void;
  /** Copy for the empty (no members at all) state. */
  emptyLabel?: string;
  /** Copy for the "search matched nothing" state. */
  noMatchLabel?: string;
  /** Copy for the error state. */
  errorLabel?: string;
  /** When false, renders a "Limited access" lock panel instead of the picker. */
  canManage?: boolean;
  /** Show the inline invite row. Default true. */
  showInvite?: boolean;
  /** Hide the per-row role badge. Default false. */
  hideRoleBadge?: boolean;
  /** Trigger placeholder when nothing is selected. */
  placeholder?: string;
  /** Search input placeholder. */
  searchPlaceholder?: string;
  /** Disable the whole control. */
  disabled?: boolean;
}

/* ----------------------------------------------------------------------------
 * Subcomponents
 * ------------------------------------------------------------------------- */

/** Role badge — tinted per role via brand tokens, with the role glyph. */
function RoleBadge({ role, size }: { role?: string; size?: "sm" | "default" }) {
  const meta = roleMeta(role);
  const Icon = meta.Icon;
  return (
    <Badge variant={meta.tone} appearance="soft" size={size === "sm" ? "sm" : "default"}>
      <Icon className="size-3" aria-hidden />
      {meta.label}
    </Badge>
  );
}

/**
 * "Added" control that reads "Remove" on hover — a pure CSS group-hover swap
 * (no JS, so it needs no reduced-motion gate). Clicking removes the member.
 */
function AddedButton({
  onRemove,
  label,
}: {
  onRemove: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onRemove}
      aria-label={`Remove ${label}`}
      className="group inline-flex min-w-[92px] flex-none items-center justify-center gap-1 rounded-full border border-transparent bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground transition-colors hover:border-destructive/25 hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 [&_svg]:size-3"
    >
      <span className="inline-flex items-center gap-1 group-hover:hidden">
        <Check aria-hidden /> Added
      </span>
      <span className="hidden items-center gap-1 group-hover:inline-flex">
        <X aria-hidden /> Remove
      </span>
    </button>
  );
}

/* ----------------------------------------------------------------------------
 * Component
 * ------------------------------------------------------------------------- */

export const MemberPicker = React.forwardRef<HTMLDivElement, MemberPickerProps>(
  (
    {
      members,
      value,
      onChange,
      onAdd,
      onInvite,
      onSearch,
      roles = DEFAULT_INVITE_ROLES,
      normalizeRole = defaultNormalizeRole,
      filterMember,
      state = "ok",
      onRetry,
      emptyLabel = "No team members yet. Invite teammates by email below.",
      noMatchLabel = "No team members match your search.",
      errorLabel = "Could not load team members.",
      canManage = true,
      showInvite = true,
      hideRoleBadge = false,
      density,
      size,
      placeholder = "Add co-creators",
      searchPlaceholder = "Search team members by name or role...",
      disabled = false,
      className,
      ...rest
    },
    ref,
  ) => {
    const reduced = useReducedMotion();
    const animate = reduced === false;

    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const [inviteEmail, setInviteEmail] = React.useState("");
    const [inviteRole, setInviteRole] = React.useState<string>(
      roles[0]?.value ?? "editor",
    );
    // Emails mid-flight on an async commit — keeps the row spinner / disabled.
    const [pending, setPending] = React.useState<Set<string>>(new Set());

    const addedEmails = React.useMemo(
      () => new Set(value.map((c) => emailOf(c).toLowerCase())),
      [value],
    );
    const isAdded = React.useCallback(
      (email?: string) => !!email && addedEmails.has(email.trim().toLowerCase()),
      [addedEmails],
    );

    // Apply the caller's exclude policy first, then the search haystack.
    const policyMembers = React.useMemo(
      () => (filterMember ? members.filter(filterMember) : members),
      [members, filterMember],
    );

    const memberEmails = React.useMemo(
      () =>
        new Set(
          policyMembers.map((m) => emailOf(m).toLowerCase()).filter(Boolean),
        ),
      [policyMembers],
    );

    // Selected-by-email rows that aren't in the member set => pending invites.
    const invitedExtras = React.useMemo(
      () => value.filter((c) => !memberEmails.has(emailOf(c).toLowerCase())),
      [value, memberEmails],
    );

    const handleSearch = (next: string) => {
      setSearch(next);
      onSearch?.(next);
    };

    const commitAdd = async (m: MemberOption) => {
      const email = emailOf(m);
      if (!email || isAdded(email)) return;
      const next: MemberOption = {
        ...m,
        email,
        role: normalizeRole(m.role),
      };
      if (!onAdd) {
        onChange([...value, next]);
        return;
      }
      const key = email.toLowerCase();
      setPending((p) => new Set(p).add(key));
      // Optimistic: show it immediately, roll back if the commit throws.
      onChange([...value, next]);
      try {
        await onAdd(next);
      } catch {
        onChange(value.filter((c) => emailOf(c).toLowerCase() !== key));
      } finally {
        setPending((p) => {
          const n = new Set(p);
          n.delete(key);
          return n;
        });
      }
    };

    const removeEmail = (email: string) =>
      onChange(value.filter((c) => emailOf(c).toLowerCase() !== email.toLowerCase()));

    const canInvite = isEmail(inviteEmail) && !isAdded(inviteEmail.trim());

    const commitInvite = async () => {
      const email = inviteEmail.trim();
      if (!isEmail(email)) return;
      if (isAdded(email)) {
        aiko.error("That email is already added.");
        return;
      }
      const next: MemberOption = {
        id: email,
        email,
        name: email,
        role: inviteRole,
        isInvite: true,
      };
      const result = onInvite?.(email, inviteRole);
      if (result && typeof (result as Promise<void>).then === "function") {
        const key = email.toLowerCase();
        setPending((p) => new Set(p).add(key));
        onChange([...value, next]);
        setInviteEmail("");
        try {
          await result;
        } catch {
          onChange(value.filter((c) => emailOf(c).toLowerCase() !== key));
        } finally {
          setPending((p) => {
            const n = new Set(p);
            n.delete(key);
            return n;
          });
        }
        return;
      }
      onChange([...value, next]);
      setInviteEmail("");
    };

    // Items the AvatarGroup + chips render. Reuse Avatar's render-priority
    // chain (src → initials) via AvatarGroupItem.
    const selectedAvatarItems: AvatarGroupItem[] = value.map((m) => ({
      name: m.name || emailOf(m),
      src: avatarOf(m),
      fallback: m.isInvite ? <Mail className="size-3.5" aria-hidden /> : undefined,
    }));

    // cmdk filters against each item's `value`. We build the four-field
    // haystack into `value` so name / username / email / role are all
    // searchable. When an async `onSearch` seam drives the data, the caller
    // owns filtering, so we turn cmdk's filter off and render as-is.
    const cmdkFilters = !onSearch;
    const haystack = (m: MemberOption): string =>
      `${m.name} ${m.username ?? ""} ${emailOf(m)} ${m.role ?? ""}`.toLowerCase();

    /* ----- Limited-access lock panel ----- */
    if (!canManage) {
      return (
        <div
          ref={ref}
          className={cn(
            "flex items-center gap-3 rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground",
            className,
          )}
          {...rest}
        >
          <Lock className="size-4 flex-none" aria-hidden />
          <span>
            You have limited access. Ask an owner or admin to manage co-creators.
          </span>
        </div>
      );
    }

    // `Row` stays the raw element (motion.div when motion is allowed, a plain
    // div otherwise) so it is the *direct* child of AnimatePresence and its
    // exit animation actually fires. Motion props come from `rowMotion`; the
    // `key` is passed directly on the JSX (never via the spread) so React 19
    // doesn't warn about a spread key.
    const Row = animate ? motion.div : "div";
    const rowMotion = animate
      ? {
          layout: true as const,
          initial: { opacity: 0, scale: 0.97 },
          animate: { opacity: 1, scale: 1 },
          exit: { opacity: 0, scale: 0.95 },
          transition: springs.gentle,
        }
      : {};

    return (
      <div ref={ref} className={cn("flex flex-col gap-2", className)} {...rest}>
        {/* Selected — AvatarGroup (fan) + removable chips */}
        {value.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <AvatarGroup
              items={selectedAvatarItems}
              size="sm"
              hoverEffect="fan"
              maxVisible={6}
            />
            <ul className="flex flex-wrap items-center gap-1.5" aria-label="Selected members">
              <AnimatePresence initial={false}>
                {value.map((m) => {
                  const chip = (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/5 py-0.5 pl-2.5 pr-1 text-xs font-medium text-foreground">
                      {m.name || emailOf(m)}
                      <button
                        type="button"
                        onClick={() => removeEmail(emailOf(m))}
                        disabled={disabled}
                        aria-label={`Remove ${m.name || emailOf(m)}`}
                        className="rounded-full p-0.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      >
                        <X className="size-3" aria-hidden />
                      </button>
                    </span>
                  );
                  return animate ? (
                    <motion.li
                      key={emailOf(m).toLowerCase()}
                      layout
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={springs.bouncy}
                    >
                      {chip}
                    </motion.li>
                  ) : (
                    <li key={emailOf(m).toLowerCase()}>{chip}</li>
                  );
                })}
              </AnimatePresence>
            </ul>
          </div>
        ) : null}

        <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              aria-label={placeholder}
              className={cn(
                "flex w-full items-center justify-between gap-2 rounded-lg border border-input bg-background px-3 py-2 text-sm text-muted-foreground",
                "transition-colors duration-180 ease-smooth-out",
                "hover:border-ring focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                "disabled:cursor-not-allowed disabled:opacity-50",
              )}
            >
              <span className="inline-flex items-center gap-2">
                <UserPlus className="size-4" aria-hidden />
                {value.length > 0 ? `${value.length} selected` : placeholder}
              </span>
            </button>
          </PopoverTrigger>

          <PopoverContent
            align="start"
            sideOffset={6}
            className="w-[var(--radix-popover-trigger-width)] min-w-72 p-0"
            // Popover's own data-state classes drive a CSS scale+fade; when
            // reduced-motion is on we collapse the duration so there is no
            // animated entrance (state is still signalled by the panel
            // appearing).
            style={animate ? undefined : { animationDuration: "0ms" }}
          >
            <Command shouldFilter={cmdkFilters} label="Search team members">
              <CommandInput
                value={search}
                onValueChange={handleSearch}
                placeholder={searchPlaceholder}
              />

              <CommandList>
                {state === "loading" ? (
                  <div className="p-1" aria-busy aria-label="Loading members">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className={cn(rowVariants({ density, size }))}>
                        <Skeleton className="size-8 flex-none rounded-full" />
                        <Skeleton className="h-2.5 flex-1 rounded" />
                      </div>
                    ))}
                  </div>
                ) : state === "error" ? (
                  <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                    {errorLabel}{" "}
                    {onRetry ? (
                      <button
                        type="button"
                        onClick={onRetry}
                        className="text-accent underline underline-offset-2 hover:text-accent/80 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      >
                        Retry
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <>
                    {/* cmdk renders this only when nothing matches. */}
                    <CommandEmpty>
                      {policyMembers.length === 0 ? emptyLabel : noMatchLabel}
                    </CommandEmpty>

                    {/* Pending invites first (added by email, not a member). */}
                    {invitedExtras.length > 0 ? (
                      <CommandGroup heading="Pending invites">
                        <AnimatePresence initial={false}>
                          {invitedExtras.map((c) => (
                            <Row key={`invite-${emailOf(c).toLowerCase()}`} {...rowMotion}>
                              <CommandItem
                                // Always visible regardless of the query — a
                                // pending invite is a committed selection.
                                value={`invite ${emailOf(c)}`}
                                onSelect={() => removeEmail(emailOf(c))}
                                className={cn(rowVariants({ density, size }), "px-2")}
                              >
                                <Avatar
                                  size="sm"
                                  fallback={<Mail className="size-3.5" aria-hidden />}
                                  name={emailOf(c)}
                                />
                                <span className="flex min-w-0 flex-1 flex-col">
                                  <span className="truncate font-medium text-foreground">
                                    {emailOf(c)}
                                  </span>
                                  <span className="truncate text-xs text-muted-foreground">
                                    Pending invite
                                  </span>
                                </span>
                                {hideRoleBadge ? null : <RoleBadge role={c.role} size={size ?? undefined} />}
                                <AddedButton
                                  onRemove={() => removeEmail(emailOf(c))}
                                  label={emailOf(c)}
                                />
                              </CommandItem>
                            </Row>
                          ))}
                        </AnimatePresence>
                      </CommandGroup>
                    ) : null}

                    {/* Workspace members. */}
                    <CommandGroup heading={invitedExtras.length > 0 ? "Members" : undefined}>
                      <AnimatePresence initial={false}>
                        {policyMembers.map((m) => {
                          const email = emailOf(m);
                          const added = isAdded(email);
                          const busy = pending.has(email.toLowerCase());
                          return (
                            <Row key={String(m.id ?? email)} {...rowMotion}>
                              <CommandItem
                                value={`${email} ${haystack(m)}`}
                                disabled={busy}
                                onSelect={() =>
                                  added ? removeEmail(email) : commitAdd(m)
                                }
                                className={cn(rowVariants({ density, size }), "px-2")}
                              >
                                <Avatar
                                  size="sm"
                                  src={avatarOf(m)}
                                  name={m.name || email}
                                  colorFromName={!avatarOf(m)}
                                />
                                <span className="flex min-w-0 flex-1 flex-col">
                                  <span className="truncate font-medium text-foreground">
                                    {m.name || email}
                                  </span>
                                  <span className="truncate text-xs text-muted-foreground">
                                    {subtitleOf(m)}
                                  </span>
                                </span>
                                {hideRoleBadge || !m.role ? null : (
                                  <RoleBadge role={m.role} size={size ?? undefined} />
                                )}
                                {added ? (
                                  <AddedButton
                                    onRemove={() => removeEmail(email)}
                                    label={m.name || email}
                                  />
                                ) : (
                                  <span
                                    className="inline-flex min-w-[92px] flex-none items-center justify-center gap-1 rounded-full border border-input bg-background px-3 py-1 text-xs font-semibold text-foreground transition-colors hover:border-accent hover:bg-accent/5 hover:text-accent [&_svg]:size-3"
                                    aria-hidden
                                  >
                                    <Plus aria-hidden /> {busy ? "Adding..." : "Add"}
                                  </span>
                                )}
                              </CommandItem>
                            </Row>
                          );
                        })}
                      </AnimatePresence>
                    </CommandGroup>
                  </>
                )}
              </CommandList>
            </Command>

            {/* Inline invite row — pinned below the list. */}
            {showInvite ? (
              <div className="border-t border-border p-3">
                <Label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-foreground">
                  <UserPlus className="size-3.5 text-muted-foreground" aria-hidden />
                  Invite new team member
                </Label>
                <div className="flex flex-col gap-2">
                  {/* Row 1: email input, full width. */}
                  <Input
                    type="email"
                    size="sm"
                    value={inviteEmail}
                    placeholder="Enter email address"
                    aria-label="Invite email address"
                    onChange={(e) => setInviteEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        void commitInvite();
                      }
                    }}
                    className="w-full"
                  />
                  {/* Row 2: role dropdown left, Invite button right. */}
                  <div className="flex items-center justify-between gap-2">
                    <Select value={inviteRole} onValueChange={setInviteRole}>
                      <SelectTrigger size="sm" aria-label="Invite role" className="flex-none">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map((r) => {
                          const meta = roleMeta(r.value);
                          const Icon = meta.Icon;
                          return (
                            <SelectItem key={r.value} value={r.value}>
                              <Icon className="size-3.5" aria-hidden />
                              {r.label}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      size="sm"
                      disabled={!canInvite}
                      onClick={() => void commitInvite()}
                      className="flex-none"
                    >
                      Invite
                    </Button>
                  </div>
                </div>
              </div>
            ) : null}
          </PopoverContent>
        </Popover>
      </div>
    );
  },
);
MemberPicker.displayName = "MemberPicker";
